import * as React from 'react';
import { MatchParams } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { PartyProps } from 'components/service/Party';
import { Theme, css } from '@emotion/react';
import { getShouldEnablePartyNotifyIndicator } from 'utils/partyUtils';
import { isOrderVariety } from '@tock/shared/utils/offerings';
import { isTouchDevice } from 'utils/touchUtils';
import { useCallback, useEffect, useRef } from 'react';
import { useRouteMatch } from 'react-router';
import Hammer from 'hammerjs';
import PartyBodyCondensed from 'components/service/party/PartyBodyCondensed';
import PartyStatusIndicator from 'components/service/party/PartyStatusIndicator';
import clsx from 'clsx';
import useDraggableParty from 'components/service/party/useDraggableParty';

const PartyCard: React.FC<React.PropsWithChildren<PartyProps>> = ({
  className,
  dragSource,
  expandable = false,
  hideTableDetails = false,
  isHD,
  isAssignable = false,
  isReadOnly,
  isSelected,
  onSelectParty,
  onSelectPartyForPreassign,
  onTogglePartyPopover,
  party,
  showGuestListNotes,
  showGuestListTags,
  showUnassignedWarning = false,
  wasSelected,
}) => {
  const route = useRouteMatch<MatchParams>();
  const containerEl = useRef<HTMLDivElement>();
  const hammerManager = useRef<HammerManager>();
  const { drag, isDragging } = useDraggableParty(party, dragSource);
  const isInTimeline = route.url.includes('calendar/day');

  const isNoShowOrCancellation =
    party.purchase?.primaryState === 'NO_SHOW' || party.purchase?.primaryState === 'CANCELED';

  const isEligibleForHoldCharge = party.purchase?.isEligibleForBookingPolicyFee;

  const isEligibleForBookingFeeRefund = party.purchase?.paymentCardHold?.status === 'HOLD_CHARGED';

  /**
   * Click handler for the party body content
   */
  const handleClick = () => {
    onSelectParty(party);
  };

  /**
   * Chooses between party selection or assignment actions when clicking on the
   * party status indicator.  The assignment action is only available
   * for expected+seated parties in service.
   */
  const handleStatusIndicatorClick = () => {
    if (
      isInTimeline ||
      !onSelectPartyForPreassign ||
      party.state === 'NO_SHOW' ||
      party.state === 'CANCELED' ||
      party.state === 'LEFT'
    ) {
      onSelectParty(party);
    } else {
      onSelectPartyForPreassign(party);
    }
  };

  /**
   * Shows the party popover menu
   */
  const handleContextMenu = useCallback(
    (e: React.MouseEvent | HammerInput) => {
      e.preventDefault();
      if (!containerEl.current) {
        return;
      }

      const { x, y } = containerEl.current.getBoundingClientRect();
      const coordinates = {
        openPositionX: x,
        openPositionY: y,
      };
      onTogglePartyPopover(party, coordinates, containerEl.current);
    },
    [party, onTogglePartyPopover]
  );

  /**
   * Callback ref for the party container.
   * The root DOM node is used to:
   *  - Initialize HammerJS
   *  - Obtain coordinates for the context menu
   */
  const containerRef = useCallback((element: HTMLDivElement) => {
    if (element) {
      if (isTouchDevice()) {
        hammerManager.current = new Hammer.Manager(element, { touchAction: 'pan-y' });
      }
      containerEl.current = element;
    }
  }, []);

  /**
   * Adds + cleans up long press listeners for touch users
   */
  useEffect(() => {
    if (hammerManager.current) {
      hammerManager.current.add(
        new Hammer.Press({
          event: 'longpress',
          time: 500,
        })
      );
      hammerManager.current.on('longpress', handleContextMenu);
    }
    return () => {
      if (hammerManager.current) {
        hammerManager.current.off('longpress');
      }
    };
  }, [hammerManager, handleContextMenu]);

  const hasStatusStyles = party.stateDescription.borderClassName;
  const partyClassnames = clsx('Party', className, {
    'is-dragging': isDragging,
    'is-expandable': !expandable,
    'Party-notify': getShouldEnablePartyNotifyIndicator(party),
    'Party-status-border': hasStatusStyles,
    [`${party.stateDescription.borderClassName}`]: hasStatusStyles && !isNoShowOrCancellation,
  });

  const partyBodyClassnames = clsx('Party-body', {
    'is-selected': isSelected,
    'is-previously-selected': wasSelected,
  });

  const hidePartyStatusIndicator =
    hideTableDetails ||
    isOrderVariety(party?.purchase?.ticketType?.variety) ||
    (isInTimeline && party.type === 'WalkIn' && party.state !== 'SEATED');

  return (
    <div
      className={partyClassnames}
      ref={containerRef}
      onContextMenu={isTouchDevice() ? undefined : handleContextMenu}
      css={(theme: Theme) => css`
        border-color: ${theme.colors.fgSubtle};
        ${isEligibleForHoldCharge &&
        !isEligibleForBookingFeeRefund &&
        `
          border-color: ${theme.colors.borderDangerBase};
        `}
      `}
    >
      <div className={partyBodyClassnames}>
        <PartyBodyCondensed
          isSelected={isSelected}
          party={party}
          onClick={handleClick}
          showGuestListNotes={showGuestListNotes}
          showGuestListTags={showGuestListTags}
          isHD={isHD}
          isEligibleForHoldCharge={isEligibleForHoldCharge}
          data-testid="party-card"
        />
      </div>

      {!hidePartyStatusIndicator && (
        <PartyStatusIndicator
          dragRef={!isReadOnly ? drag : undefined}
          onClick={handleStatusIndicatorClick}
          isAssignmentEnabled={isAssignable}
          party={party}
          showUnassignedWarning={showUnassignedWarning}
        />
      )}
    </div>
  );
};

export default PartyCard;
